import React, { useState, useRef, useEffect, cloneElement, isValidElement } from 'react';
import {
  View,
  TouchableOpacity,
  Modal,
  TouchableWithoutFeedback,
  Platform,
  ViewStyle,
  LayoutRectangle,
} from 'react-native';
import { useTheme } from '../context/themeContext';
import { useResponsive } from '../lib/responsive';

interface PopoverProps {
  trigger: React.ReactElement;
  children: React.ReactNode | ((close: () => void) => React.ReactNode);
  align?: 'left' | 'right';
  width?: number;
  // 'down' (default) opens below the trigger; 'up' opens above it (for
  // triggers near the bottom of the screen, e.g. the sidebar footer).
  placement?: 'down' | 'up';
  style?: ViewStyle;
}

export default function Popover({ trigger, children, align = 'left', width, placement = 'down', style }: PopoverProps) {
  const { theme } = useTheme();
  const { colors, radii, shadows } = theme;
  const { width: screenWidth, height: screenHeight } = useResponsive();
  const [open, setOpen] = useState(false);
  const [layout, setLayout] = useState<LayoutRectangle | null>(null);
  const triggerRef = useRef<View>(null);

  const close = () => setOpen(false);
  const toggle = () => setOpen((o) => !o);

  // Web: handle Escape key
  useEffect(() => {
    if (Platform.OS !== 'web' || !open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const onTriggerLayout = () => {
    if (triggerRef.current) {
      triggerRef.current.measure((_x, _y, w, h, pageX, pageY) => {
        setLayout({ x: pageX, y: pageY, width: w, height: h });
      });
    }
  };

  const triggerWithPress = isValidElement(trigger)
    ? cloneElement(trigger as React.ReactElement<any>, {
        onPress: (e: any) => {
          onTriggerLayout();
          toggle();
          (trigger.props as any).onPress?.(e);
        },
      })
    : trigger;

  const dropdownWidth = width ?? 200;

  let left: number | undefined = layout?.x;
  let right: number | undefined = undefined;

  if (align === 'right' && layout) {
    right = screenWidth - layout.x - layout.width;
    left = undefined;
  }

  // Down: anchor top under the trigger. Up: anchor bottom above the trigger.
  const vertical: ViewStyle =
    placement === 'up'
      ? { bottom: layout ? screenHeight - layout.y + 4 : 0 }
      : { top: layout ? layout.y + layout.height + 4 : 0 };

  const panelStyle: ViewStyle = {
    position: 'absolute',
    ...vertical,
    ...(left !== undefined ? { left } : {}),
    ...(right !== undefined ? { right } : {}),
    minWidth: dropdownWidth,
    ...(width ? { width: dropdownWidth } : {}),
    maxHeight: '70%' as any,
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r3,
    padding: 6,
    zIndex: 40,
    ...shadows.lg,
  };

  // Both web + native render the dropdown via a Modal (portal at the page
  // root), so it is never clipped by an ancestor's overflow/transform — which
  // was clipping the inline `position:absolute` dropdown inside the app shell.

  // Native: use Modal for overlay
  return (
    <View ref={triggerRef} style={style}>
      {triggerWithPress}
      {open && layout && (
        <Modal transparent visible animationType="fade" onRequestClose={close}>
          <TouchableWithoutFeedback onPress={close}>
            <View style={{ flex: 1 }}>
              <TouchableWithoutFeedback>
                <View style={panelStyle}>
                  {typeof children === 'function' ? children(close) : children}
                </View>
              </TouchableWithoutFeedback>
            </View>
          </TouchableWithoutFeedback>
        </Modal>
      )}
    </View>
  );
}
